// Dependency-free SVG charts in a calm "financial terminal" style: a thin line over a faint flat fill,
// sparse gridlines, right-hand y-axis in tabular numerals, crosshair readout on hover and keyboard focus.
import './chart.css';
import { useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';

const defaultFormat = (v) => (Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10));
const toValue = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function niceStep(raw) {
  if (!(raw > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(raw));
  const f = raw / power;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * power;
}

const clean = (v) => Math.round(v * 1e9) / 1e9;

// Ticks covering [lo, hi] (auto domain) or inside it (fixed domain).
export function niceTicks(lo, hi, { count = 4, integer = false, fixed = false } = {}) {
  let step = niceStep((hi - lo) / count);
  if (integer) step = Math.max(1, Math.round(step));
  const first = fixed ? Math.ceil(lo / step) * step : Math.floor(lo / step) * step;
  const last = fixed ? Math.floor(hi / step) * step : Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = first; v <= last + step / 2; v += step) ticks.push(clean(v));
  return ticks;
}

// Consecutive non-null points; a null value breaks the line (or, with `connect`, is bridged).
function segments(points, connect = false) {
  if (connect) {
    const run = points.filter((p) => p.y != null);
    return run.length ? [run] : [];
  }
  const out = [];
  let run = [];
  for (const p of points) {
    if (p.y == null) {
      if (run.length) out.push(run);
      run = [];
    } else run.push(p);
  }
  if (run.length) out.push(run);
  return out;
}

const f1 = (v) => v.toFixed(1);
const straightPath = (seg) => seg.map((p, i) => `${i ? 'L' : 'M'}${f1(p.x)} ${f1(p.y)}`).join('');
// Monotone cubic (Fritsch–Carlson): a smooth curve that never overshoots the data (no dips below zero).
function smoothPath(seg) {
  const n = seg.length;
  if (n < 3) return straightPath(seg);
  const dx = [];
  const m = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = seg[i + 1].x - seg[i].x;
    m[i] = dx[i] ? (seg[i + 1].y - seg[i].y) / dx[i] : 0;
  }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) {
    t[i] = m[i - 1] * m[i] <= 0 ? 0 : (3 * (dx[i - 1] + dx[i])) / ((2 * dx[i] + dx[i - 1]) / m[i - 1] + (dx[i] + 2 * dx[i - 1]) / m[i]);
  }
  t[n - 1] = m[n - 2];
  let d = `M${f1(seg[0].x)} ${f1(seg[0].y)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += `C${f1(seg[i].x + h)} ${f1(seg[i].y + t[i] * h)} ${f1(seg[i + 1].x - h)} ${f1(seg[i + 1].y - t[i + 1] * h)} ${f1(seg[i + 1].x)} ${f1(seg[i + 1].y)}`;
  }
  return d;
}
const linePath = (seg, smooth = false) => (smooth ? smoothPath(seg) : straightPath(seg));
const areaPath = (seg, baseY, smooth = false) => `${linePath(seg, smooth)}L${f1(seg[seg.length - 1].x)} ${f1(baseY)}L${f1(seg[0].x)} ${f1(baseY)}Z`;

function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') {
      const onResize = () => setWidth(el.clientWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(el.clientWidth));
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(frame); ro.disconnect(); };
  }, []);
  return width;
}

const PAD_TOP = 12;
const PAD_BOTTOM = 26;
const EDGE = 2; // keeps the first/last point's stroke inside the plot

// x-axis label anchors, and a label's horizontal extent (11px tabular axis text ≈ 6.6px per character).
const startAnchor = (x, right) => (x < 60 ? 'start' : x > right - 60 ? 'end' : 'middle');
const tickAnchor = (i, x, n, right) => (i === 0 && n > 1 ? 'start' : x > right - 30 ? 'end' : 'middle');
function labelExtent(x, text, anchor) {
  const w = String(text).length * 6.6;
  return anchor === 'start' ? [x, x + w] : anchor === 'end' ? [x - w, x] : [x - w / 2, x + w / 2];
}

// data: [{ label, value }] (value null = gap). tone: 'neutral' | 'success' | 'danger'.
// yDomain: [min, max] to fix the scale (e.g. [0, 100] for percentages); otherwise 0-based and rounded up.
// connectNulls: draw the line straight across null values instead of breaking it (the x-axis keeps every point).
// smooth: a monotone curve instead of straight segments (sparse counts read as a trend, not as spikes).
// markStart: when the data begins later than the range (leading nulls), a dashed guide + "From <label>" marks where the
// line starts, so the empty left part reads as "no data yet" rather than a gap.
export function AreaChart({ data = [], height = 200, format = defaultFormat, yDomain, tone = 'neutral', label = 'Chart', emptyText = 'No data for this period.', connectNulls = false, smooth = false, markStart = false, class: cls }) {
  const wrapRef = useRef(null);
  const width = useWidth(wrapRef);
  const [active, setActive] = useState(-1);
  const values = data.map((d) => toValue(d.value));
  const finite = values.filter((v) => v != null);

  const geo = useMemo(() => {
    if (!width || !finite.length) return null;
    let lo;
    let hi;
    let ticks;
    if (yDomain) {
      [lo, hi] = yDomain;
      ticks = niceTicks(lo, hi, { fixed: true });
    } else {
      lo = Math.min(0, ...finite);
      hi = Math.max(...finite);
      if (hi === lo) hi = lo + 1;
      ticks = niceTicks(lo, hi, { integer: finite.every(Number.isInteger) });
      lo = ticks[0];
      hi = ticks[ticks.length - 1];
    }
    const labels = ticks.map((t) => format(t));
    const axisW = Math.max(...labels.map((l) => l.length)) * 6.6 + 14;
    const right = Math.max(40, width - axisW);
    const plotH = height - PAD_TOP - PAD_BOTTOM;
    const n = data.length;
    const xAt = (i) => (n === 1 ? right / 2 : EDGE + (i * (right - EDGE * 2)) / (n - 1));
    const yAt = (v) => PAD_TOP + (1 - (Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * plotH;
    const points = values.map((v, i) => ({ x: xAt(i), y: v == null ? null : yAt(v), i }));
    const baseY = yAt(lo <= 0 && hi >= 0 ? 0 : lo);
    const maxLabels = Math.max(2, Math.floor(right / 76));
    const step = Math.ceil(n / maxLabels);
    const xLabels = [];
    for (let i = 0; i < n; i += step) xLabels.push(i);
    const firstIdx = values.findIndex((v) => v != null);
    const start = markStart && firstIdx > 0 && firstIdx < n - 1 ? points[firstIdx] : null;
    // No x-axis label that would touch the "From …" label (its real extent, anchor included).
    let xl = xLabels;
    if (start) {
      const [a, b] = labelExtent(start.x, `From ${data[start.i].label}`, startAnchor(start.x, right));
      xl = xLabels.filter((i) => {
        const [c, d] = labelExtent(points[i].x, data[i].label, tickAnchor(i, points[i].x, n, right));
        return d + 10 < a || c > b + 10;
      });
    }
    return { ticks, labels, right, plotH, points, baseY, xLabels: xl, yAt, start, segs: segments(points, connectNulls) };
  }, [width, height, data, yDomain, format, connectNulls, markStart]);

  const pickIndex = (clientX) => {
    const svg = wrapRef.current?.querySelector('svg');
    if (!svg || !geo) return -1;
    const x = clientX - svg.getBoundingClientRect().left;
    let best = 0;
    for (const p of geo.points) if (Math.abs(p.x - x) < Math.abs(geo.points[best].x - x)) best = p.i;
    return best;
  };

  const onKeyDown = (e) => {
    const n = data.length;
    if (!n) return;
    const from = active < 0 ? n - 1 : active;
    const next = { ArrowLeft: Math.max(0, from - 1), ArrowRight: Math.min(n - 1, from + 1), Home: 0, End: n - 1 }[e.key];
    if (e.key === 'Escape' && active >= 0) { e.preventDefault(); setActive(-1); return; }
    if (next === undefined) return;
    e.preventDefault();
    setActive(next);
  };

  const activePoint = geo && active >= 0 ? geo.points[active] : null;
  const readout = active >= 0 && data[active] ? `${data[active].label}: ${values[active] == null ? 'No data' : format(values[active])}` : '';
  // The readout sits beside the crosshair, flipping to the left in the right part of the plot.
  const tipSide = activePoint && activePoint.x > geo.right * 0.6 ? 'left' : 'right';

  return (
    <div
      ref={wrapRef}
      class={['chart', `chart-${tone}`, cls].filter(Boolean).join(' ')}
      style={{ height: `${height}px` }}
      tabIndex={finite.length ? 0 : undefined}
      role="group"
      aria-roledescription="chart"
      aria-label={`${label}. Use the left and right arrow keys to read values.`}
      onKeyDown={onKeyDown}
      onFocus={() => { if (active < 0 && data.length) setActive(data.length - 1); }}
      onBlur={() => setActive(-1)}
    >
      {!finite.length && <div class="chart-empty">{emptyText}</div>}
      {geo && (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          aria-hidden="true"
          onPointerMove={(e) => setActive(pickIndex(e.clientX))}
          onPointerDown={(e) => setActive(pickIndex(e.clientX))}
          onPointerLeave={() => { if (document.activeElement !== wrapRef.current) setActive(-1); }}
        >
          {geo.ticks.map((t, i) => {
            const y = Math.round(geo.yAt(t)) + 0.5;
            return (
              <g key={t}>
                <line class="chart-grid" x1="0" x2={geo.right} y1={y} y2={y} />
                <text class="chart-axis" x={width} y={y} text-anchor="end" dominant-baseline="middle">{geo.labels[i]}</text>
              </g>
            );
          })}
          {geo.segs.map((seg, i) => (
            <g key={i}>
              {seg.length > 1 && <path class="chart-area" d={areaPath(seg, geo.baseY, smooth)} />}
              {seg.length > 1 ? <path class="chart-line" d={linePath(seg, smooth)} /> : <circle class="chart-point" cx={seg[0].x} cy={seg[0].y} r="2" />}
            </g>
          ))}
          {geo.start && (
            <g class="chart-start">
              <line class="chart-start-line" x1={geo.start.x} x2={geo.start.x} y1={PAD_TOP} y2={PAD_TOP + geo.plotH} />
              <circle class="chart-start-dot" cx={geo.start.x} cy={geo.start.y} r="3" />
              <text class="chart-axis chart-start-label" x={geo.start.x} y={height - 6}
                text-anchor={startAnchor(geo.start.x, geo.right)}>
                From {data[geo.start.i].label}
              </text>
            </g>
          )}
          {geo.xLabels.map((i) => {
            const x = geo.points[i].x;
            const anchor = tickAnchor(i, x, data.length, geo.right);
            return <text key={i} class="chart-axis" x={x} y={height - 6} text-anchor={anchor}>{data[i].label}</text>;
          })}
          {activePoint && (
            <g class="chart-focus">
              <line class="chart-crosshair" x1={activePoint.x} x2={activePoint.x} y1={PAD_TOP - 4} y2={PAD_TOP + geo.plotH} />
              {activePoint.y != null && <circle class="chart-dot" cx={activePoint.x} cy={activePoint.y} r="3.5" />}
            </g>
          )}
        </svg>
      )}
      {activePoint && (
        <div class={`chart-tip chart-tip-${tipSide}`} style={{ left: `${activePoint.x}px` }} aria-hidden="true">
          <strong>{values[active] == null ? '—' : format(values[active])}</strong>
          <span>{data[active].label}</span>
        </div>
      )}
      <span class="sr-only" aria-live="polite">{readout}</span>
      {/* A <table> ignores .sr-only's 1px box (it would add blank scroll below the chart), so a div carries it. */}
      <div class="sr-only">
        <table>
          <caption>{label}</caption>
          <tbody>
            {data.map((d, i) => (
              <tr key={i}><th scope="row">{d.label}</th><td>{values[i] == null ? 'No data' : format(values[i])}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Inline trend line; values may contain nulls (gaps).
export function Sparkline({ values = [], tone = 'neutral', width = 80, height = 24, label }) {
  const nums = values.map(toValue);
  const finite = nums.filter((v) => v != null);
  const pad = 2.5;
  let body = null;
  if (finite.length) {
    const lo = Math.min(...finite);
    const hi = Math.max(...finite);
    const span = hi - lo || 1;
    const n = nums.length;
    const pts = nums.map((v, i) => ({
      x: n === 1 ? width / 2 : pad + (i * (width - pad * 2)) / (n - 1),
      y: v == null ? null : hi === lo ? height / 2 : pad + (1 - (v - lo) / span) * (height - pad * 2),
      i,
    }));
    const last = [...pts].reverse().find((p) => p.y != null);
    body = (
      <>
        {segments(pts).map((seg, i) => (seg.length > 1
          ? <path key={i} class="chart-line" d={linePath(seg)} />
          : <circle key={i} class="chart-point" cx={seg[0].x} cy={seg[0].y} r="1.5" />))}
        <circle class="spark-end" cx={last.x} cy={last.y} r="2" />
      </>
    );
  }
  return (
    <svg
      class={`sparkline chart-${tone}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : 'true'}
    >
      {body}
    </svg>
  );
}
